import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { getSlug } from 'actions/slug';
import { useI18n } from 'context/i18n';
import { useLocale } from 'hooks';
import { useCountries } from 'hooks/useCountries';
import { setGBCookie } from 'utils/i18n';

import { Dialog, DialogHeader, DialogRoot } from 'components/ui/dialog';
import { ComboBox } from 'components/ui/combo-box';
import { Select } from 'components/ui/select';
import { getDomainValue, isDomain } from 'utils/domain';
import { Button } from 'components/ui/button';

type LanguageSwitcherProps = {
  translateSlug?: boolean;
};

const isLab = isDomain('camperlab');

export function LanguageSwitcher({ translateSlug = false }: LanguageSwitcherProps) {
  const { t, countries: defaultCountries } = useI18n();
  const { asPath } = useRouter();
  const { language: defaultLanguage, country: defaultCountry } = useLocale();
  const { isBritishCountry } = useCountries();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const countries = useMemo(() => defaultCountries.filter((c) => Array.isArray(c.languages) && c.languages.length > 0), [defaultCountries]);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const foundCountry = countries.find((item) => item.id === defaultCountry);
    return {
      selectedKey: foundCountry?.id ?? null,
      inputValue: foundCountry?.name ?? '',
    };
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => defaultLanguage);
  const countryData = countries.find((item) => item.id === selectedCountry.selectedKey);

  useEffect(() => {
    window.openLocaleSwitcher = () => {
      setIsOpen(true);
    };
    return () => {
      window.openLocaleSwitcher = undefined;
    };
  }, []);

  const onSelectionChange = (id: string) => {
    const foundCountry = countries.find((o) => o.id === id);
    setSelectedCountry({
      inputValue: foundCountry?.name ?? '',
      selectedKey: id,
    });

    if (foundCountry?.languages.length) {
      const defaultLanguageFromCountry = foundCountry?.languages?.find((item) => item.id === defaultLanguage)?.id ?? foundCountry?.languages?.[0]?.id;
      setSelectedLanguage(defaultLanguageFromCountry);
    }
  };

  const onInputChange = (value: string) => {
    setSelectedCountry((prevState) => ({
      inputValue: value,
      selectedKey: value === '' ? null : prevState.selectedKey,
    }));
  };

  const onSubmit = async () => {
    const targetCountry = selectedCountry.selectedKey;
    const targetLanguage = selectedLanguage;
    // no deberia de pasar
    if (!targetCountry || !targetLanguage) {
      console.error(`No country(${targetCountry}) or language(${targetLanguage}) selected `);
      return;
    }
    if (targetCountry === defaultCountry && targetLanguage === defaultLanguage) {
      setIsOpen(false);
      return;
    }
    setIsSubmitting(true);
    if (isBritishCountry(selectedCountry.selectedKey)) {
      setGBCookie(selectedCountry.selectedKey);
    }
    let redirect = asPath;
    try {
      if (translateSlug) {
        redirect = await getSlug({ lang: defaultLanguage, targetLanguage: selectedLanguage, slug: asPath });
      }
      window.location.assign(`${window.location.origin}/${targetLanguage}_${targetCountry}${redirect}`);
    } catch (error) {
      window.location.assign(`${window.location.origin}/${targetLanguage}_${targetCountry}/`);
    }
  };

  return (
    <DialogRoot
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        setIsOpen(isOpen);
      }}
      showOverlay
    >
      <Dialog>
        <DialogHeader label={t('modal.idiomas', 'welcome', 'Welcome')} hidden={!isLab} />
        {!isLab ?
          <p aria-hidden className="mt-4 text-center text-2xl lab:mt-0 lab:text-left lab:text-sm lab:font-bold lab:uppercase lab:lg:text-xs">
            {t('modal.idiomas', 'welcome', 'Welcome')}.
          </p>
        : null}
        <div className="mx-auto mt-4 flex max-w-80 flex-col gap-2 pb-2 lab:pb-0 sm:pb-12 lab:sm:pb-0">
          <ComboBox
            menuTrigger="focus"
            selectedKey={selectedCountry.selectedKey}
            inputValue={selectedCountry.inputValue}
            onInputChange={onInputChange}
            onSelectionChange={(key) => {
              onSelectionChange(key as string);
            }}
            label={t('modal.idiomas', 'select.country', 'Select a country')}
            defaultItems={countries.map((country) => ({ label: country.name, value: country.id }))}
          />
          <Select
            label={t('modal.idiomas', 'select.language', 'Select language')}
            variant={getDomainValue({ camper: 'box', nnormal: 'box', camperlab: 'default' })}
            items={countryData?.languages.map((language) => ({ label: language.name, value: language.id })) ?? []}
            selectedKey={selectedLanguage}
            onSelectionChange={(key) => {
              setSelectedLanguage(key as string);
            }}
          />
          <Button isPending={isSubmitting} onPress={onSubmit}>
            {t('modal.idiomas', 'submit', 'Submit')}
          </Button>
        </div>
      </Dialog>
    </DialogRoot>
  );
}
